import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import ByzMuiSearchButton from "src/components/core-components/mui/buttons/ByzMuiSearchButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import HistoryGrid from "src/components/shared-components/history-grid/HistoryGrid";
import { GridColDef, GridToolbar } from "@mui/x-data-grid-premium";
import { useTranslate } from "src/locales";

interface RequestServiceGridAreaProps {
  currentView: string;
  selectedSubCategory: string;
  mainSearchTerm: string;
  setMainSearchTerm: (val: string) => void;
  requestNote: string;
  setRequestNote: (val: string) => void;
  urgencyLevel: number;
  setUrgencyLevel: (val: number) => void;
  procedureDate: Date;
  setProcedureDate: (val: Date) => void;
  isPregnant: boolean;
  setIsPregnant: (val: boolean) => void;
  
  // Data
  gridData: any[];
  flattenedHistoryData: any[];
  historyDataLength: number;
  
  // Loading & State
  loadingServices: boolean;
  isServiceProcessing: boolean;
  loadingHistoryData: boolean;
  
  // History Pagination
  historyParentPage: number;
  setHistoryParentPage: (val: number) => void;
  historyParentPageSize: number;
  setHistoryParentPageSize: (val: number) => void;
  
  // Selection
  selectedServices: (number | string)[];
  selectedHistoryServices: (number | string)[];
  expandedHistoryRows: string[];
  favoriteServiceIds: Set<number>;
  
  // Actions
  onClose: () => void;

  handleServiceToggle: (id: number | string) => void;
  handleHistoryServiceToggle: (id: number | string, data?: any) => void;
  handleHistoryParentToggle: (id: number | string) => void;
  handleParentToggle: (id: string) => void; // For HistoryGrid prop name match? Check HistoryGrid props.
  handleAddToFavorites: (id: number | string) => Promise<void>;
  handleRemoveFromFavorites: (id: number | string) => Promise<void>;
}

const RequestServiceGridArea: React.FC<RequestServiceGridAreaProps> = ({
  currentView,
  selectedSubCategory,
  mainSearchTerm,
  setMainSearchTerm,
  requestNote,
  setRequestNote,
  urgencyLevel,
  setUrgencyLevel,
  procedureDate,
  setProcedureDate,
  isPregnant,
  setIsPregnant,
  gridData,
  flattenedHistoryData,
  historyDataLength,
  loadingServices,
  isServiceProcessing,
  loadingHistoryData,
  historyParentPage,
  setHistoryParentPage,
  historyParentPageSize,
  setHistoryParentPageSize,
  selectedServices,
  selectedHistoryServices,
  expandedHistoryRows,
  favoriteServiceIds,
  onClose,

  handleServiceToggle,
  handleHistoryServiceToggle,
  handleHistoryParentToggle,
  handleParentToggle,
  handleAddToFavorites,
  handleRemoveFromFavorites,
}) => {
  const { t } = useTranslate();

  const isServiceInFavorites = (rdId: number): boolean => {
    if (!rdId || rdId === undefined) return false;
    return favoriteServiceIds.has(rdId);
  };

  // Filter grid data based on mainSearchTerm
  const filteredGridData = React.useMemo(() => {
    if (!mainSearchTerm || mainSearchTerm.trim() === "") {
      return gridData;
    }
    const searchLower = mainSearchTerm.toLocaleLowerCase("tr-TR");
    return gridData.filter((row: any) => {
      const name = (row.name || row.process_name || "").toLocaleLowerCase("tr-TR");
      const code = (row.code || "").toLocaleLowerCase("tr-TR");
      return name.includes(searchLower) || code.includes(searchLower);
    });
  }, [gridData, mainSearchTerm]);

  const columns: GridColDef[] = [
    {
      field: "select",
      headerName: "",
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minWidth: "120px",
            height: "40px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              height: "56px",
              width: "1px",
              backgroundColor: "#666",
              zIndex: 1,
            }}
          />
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Checkbox
            checked={selectedServices.includes(params.row.process_id || params.row.id)}
            onChange={() => handleServiceToggle(params.row.id)}
            sx={{
              color: "#333",
              "&.Mui-checked": {
                color: "#333",
              },
            }}
          />
          <ByzMuiOpenProcessMenu
            menuItems={
              currentView === "frequent"
                ? [
                    {
                      label: t("patient-request-service.favorites.remove"),
                      iconColor: "error",
                      onClick: async () =>
                        handleRemoveFromFavorites(params.row.id),
                    },
                  ]
                : isServiceInFavorites(params.row.rdId || params.row.id)
                ? [
                    {
                      label: t("patient-request-service.favorites.remove"),
                      iconColor: "error",
                      onClick: async () => {
                        handleRemoveFromFavorites(params.row.id);
                      },
                    },
                  ]
                : [
                    {
                      label: t("patient-request-service.favorites.add"),
                      iconColor: "success",
                      onClick: async () => {
                        handleAddToFavorites(params.row.id);
                      },
                    },
                  ]
            }
            onClose={() => {}}
            sx={{ marginRight: "8px" }}
          />
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: "#666",
            }}
          />
        </Box>
      ),
    },
    {
      field: "name",
      headerName: t("patient-request-service.grid.columns.operation-name"),
      flex: 1,
      minWidth: 300,
    },
    {
      field: "note",
      headerName: t("patient-request-service.grid.columns.note"),
      width: 150,
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        p: { xs: 1, sm: 2 },
        position: "relative",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={500}
          sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
        >
          {t("patient-request-service.new-service")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ByzMuiCloseButton onClick={onClose} />
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 3, alignItems: "center" }}
      >
        <TextField
          size="small"
          placeholder={t("patient-request-service.search.filter")}
          value={mainSearchTerm}
          onChange={(e) => {
            setMainSearchTerm(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small">
                  <FilterListIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 500, md: 660 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f4f5f7",
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Box
          sx={{
            width: "30px",
            height: "30px",
            padding: "1px",
            scale: "0.8",
            mb: "20px",
          }}
        >
          <ByzMuiSearchButton />
        </Box>
      </Stack>

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 0.5 }}>
        <Grid item xs={12} sm={6} md={2.1}>
          <TextField
            fullWidth
            size="small"
            label={t("patient-request-service.form.note")}
            placeholder={t("patient-request-service.form.note-placeholder")}
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#2498f3",
                },
                "&:hover fieldset": {
                  borderColor: "#2498f3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2498f3",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#2498f3",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#2498f3",
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.1}>
          <ByzMuiFormSelect
            label={t("patient-request-service.form.urgent")}
            value={urgencyLevel}
            onChange={(e) => setUrgencyLevel(Number(e.target.value))}
          >
            <MenuItem value={0}>
              {t("patient-request-service.form.urgency-levels.normal")}
            </MenuItem>
            <MenuItem value={1}>
              {t(
                "patient-request-service.form.urgency-levels.probable-urgent"
              )}
            </MenuItem>
            <MenuItem value={2}>
              {t("patient-request-service.form.urgency-levels.urgent")}
            </MenuItem>
            <MenuItem value={3}>
              {t(
                "patient-request-service.form.urgency-levels.very-urgent"
              )}
            </MenuItem>
          </ByzMuiFormSelect>
        </Grid>
        <Grid item xs={12} sm={6} md={2.1}>
          <ByzMuiDateTimePicker
            label={t("patient-request-service.form.procedure-date")}
            value={procedureDate}
            onChange={(newValue: any) =>
              setProcedureDate(newValue || new Date())
            }
            minDateTime={new Date()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isPregnant}
                onChange={(e) => setIsPregnant(e.target.checked)}
              />
            }
            label={t("patient-request-service.form.pregnant")}
            sx={{ mt: 1 }}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          width: "100%",
          mb: 0,
          pb: 0,
          height: "65vh",
          overflow: "hidden",
          "& .MuiDataGrid-root": { mb: 0, height: "100%" },
        }}
      >
        {currentView === "history" ? (
          <HistoryGrid
            rows={flattenedHistoryData}
            selectedHistoryServices={selectedHistoryServices}
            expandedHistoryRows={expandedHistoryRows}
            historyParentPage={historyParentPage}
            historyParentPageSize={historyParentPageSize}
            totalParentCount={historyDataLength}
            loading={loadingHistoryData}
            onHistoryServiceToggle={handleHistoryServiceToggle}
            onHistoryParentToggle={handleHistoryParentToggle}
            onParentToggle={handleParentToggle}
            onHistoryParentPageChange={(event: any, newPage: number) => {
              setHistoryParentPage(newPage);
            }}
            onHistoryParentRowsPerPageChange={(event: any) => {
              setHistoryParentPageSize(parseInt(event.target.value, 10));
              setHistoryParentPage(0);
            }}
            onAddToFavorites={async (id) => await handleAddToFavorites(id)}
            showAddToFavorites={true}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              width: "100%",
              maxHeight: "65vh", // maksimum yükseklik
              display: "flex",
              flexDirection: "column",
              overflow: "auto", // Box'ta scroll yap
            }}
          >
            <ByzMuiDataGrid
              rows={filteredGridData}
              columns={columns}
              pagination
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              getRowId={(row: any) => row.id}
              checkboxSelection={false}
              disableColumnMenu={false}
              autoHeight
              getRowHeight={() => 40}
              enableRowZoom={true}
              loading={loadingServices || isServiceProcessing}
              disableVirtualization={false}
              rowSelectionModel={[]}
              hideFooterSelectedRowCount={true}
              whiteGrid={true}
              slots={{
                toolbar: GridToolbar,
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      minHeight: 200,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t("patient-request-service.grid.no-services")}
                    </Typography>
                    <Typography variant="body2">
                      {currentView === "frequent"
                        ? t("patient-request-service.grid.no-favorites")
                        : currentView === "history"
                        ? t("patient-request-service.grid.no-history")
                        : currentView === "subcategory" &&
                          selectedSubCategory
                        ? t(
                            "patient-request-service.grid.no-services-in-category"
                          )
                        : t("patient-request-service.grid.select-category")}
                    </Typography>
                  </Box>
                ),
              }}
              sx={{
                width: "100%",
                pointerEvents: (loadingServices || isServiceProcessing) ? "none" : "auto",
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RequestServiceGridArea;
